import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import propTypes from "@styled-system/prop-types";
import { useController } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons";
import { pick, omit } from "lodash";
import { Column } from "../grid";
import { CardRadioStyle, CardRadioOptionStyle, CardRadioErrorStyle } from "./formCardRadio.style";
import {
  FormRadioGroup,
  FormRadioWrapper,
  FormRadioLabelStyle,
  FormLabelStyle,
  FormRadioStyle,
  FormHelpStyle, FormInvalidFeedbackStyle,
} from "./forms.style";

const CardRadioOption = ({
  children,
  name,
  value,
  disabled,
  checked,
  onChange,
  ...attributes
}) => {
  const optionContentRef = useRef(null);

  const onInputChange = (e) => {
    onChange(e);
  };

  const optionClickHandler = (e) => {
    const input = optionContentRef.current.querySelector("input");

    if (e.target.tagName.toLowerCase() !== "input" && input instanceof Element && !disabled) {
      input.click();
    }
  };

  return (
    <CardRadioOptionStyle
      ref={optionContentRef}
      className={checked ? "selected" : ""}
      tabIndex={checked ? "0" : "-1"}
      disabled={disabled}
      onClick={optionClickHandler}
      {...attributes}
    >
      <div className="card-radio-option-content">
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onInputChange}
          style={{ display: "none" }}
          disabled={disabled}
        />
        {children}
      </div>
    </CardRadioOptionStyle>
  );
};

CardRadioOption.propTypes = {
  children: PropTypes.node,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  checked: PropTypes.bool,
  clickHandler: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

CardRadioOption.defaultProps = {
  value: "",
  disabled: false,
  checked: false,
};

const FormRadio = ({
  innerRef,
  variant,
  validationRules,
  watch,
  control,
  formState,
  name,
  options,
  valueProp,
  textProp,
  defaultValue,
  label,
  id,
  helpText,
  onChange,

  /* Regular radio props */
  inline,

  /* Card radio props */
  itemsPerRowXs,
  itemsPerRowSm,
  itemsPerRowMd,
  itemsPerRowLg,
  itemsPerRowXl,
  enableAddOption,
  addOptionText,
  onAddOptionClick,
  ...props
}) => {
  const [isRequired] = useState(!!validationRules?.required);
  const [error, setError] = useState(formState.errors ? formState.errors[name] : undefined);
  const [columnProps, setColumnProps] = useState({ xs: { size: 12 } });
  const inputId = id || `radio_${uuidv4()}`;
  const spacePropsKeys = Object.keys(propTypes.space);
  const typographyPropsKeys = Object.keys(propTypes.typography);
  const spaceProps = pick(props, spacePropsKeys);
  const typographyProps = pick(props, typographyPropsKeys);
  const attributes = omit(props, [...spacePropsKeys, ...typographyPropsKeys]);
  const addOptionContentRef = useRef(null);
  const {
    field: { onChange: oc, onBlur: ob, value },
    meta: { invalid },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: defaultValue || "",
  });

  const onRadioChange = (e) => {
    oc(e.target.value);
    onChange && onChange(e);
  };

  const addOptionClickHandler = (e) => {
    const input = addOptionContentRef.current.querySelector("input");

    if (e.target.tagName.toLowerCase() !== "input" && input instanceof Element) {
      input.click();
      oc({ value: input.value });
      onAddOptionClick && onAddOptionClick(e);
    }
  };

  const localItemsPerRowXs = useMemo(() => itemsPerRowXs || 1, [itemsPerRowXs]);

  const localItemsPerRowSm = useMemo(() => itemsPerRowSm || itemsPerRowXs || 1, [itemsPerRowXs, itemsPerRowSm]);

  const localItemsPerRowMd = useMemo(() =>
    itemsPerRowMd || itemsPerRowSm || itemsPerRowXs || 1,
    [itemsPerRowXs, itemsPerRowSm, itemsPerRowMd]);

  const localItemsPerRowLg = useMemo(() =>
    itemsPerRowLg || itemsPerRowMd || itemsPerRowSm || itemsPerRowXs || 1,
    [itemsPerRowXs, itemsPerRowSm, itemsPerRowMd, itemsPerRowLg]);

  const localItemsPerRowXl = useMemo(() =>
    itemsPerRowXl || itemsPerRowLg || itemsPerRowMd || itemsPerRowSm || itemsPerRowXs || 1,
    [itemsPerRowXs, itemsPerRowSm, itemsPerRowMd, itemsPerRowLg, itemsPerRowXl]);

  useEffect(() => {
    const columnClasses = [12, 12, 12, 12, 12];

    if (itemsPerRowXs) {
      columnClasses[0] = 12 / itemsPerRowXs;
      columnClasses[1] = 12 / itemsPerRowXs;
      columnClasses[2] = 12 / itemsPerRowXs;
      columnClasses[3] = 12 / itemsPerRowXs;
      columnClasses[4] = 12 / itemsPerRowXs;
    }

    if (itemsPerRowSm) {
      columnClasses[1] = 12 / itemsPerRowSm;
      columnClasses[2] = 12 / itemsPerRowSm;
      columnClasses[3] = 12 / itemsPerRowSm;
      columnClasses[4] = 12 / itemsPerRowSm;
    }

    if (itemsPerRowMd) {
      columnClasses[2] = 12 / itemsPerRowMd;
      columnClasses[3] = 12 / itemsPerRowMd;
      columnClasses[4] = 12 / itemsPerRowMd;
    }

    if (itemsPerRowLg) {
      columnClasses[3] = 12 / itemsPerRowLg;
      columnClasses[4] = 12 / itemsPerRowLg;
    }

    if (itemsPerRowXl) {
      columnClasses[4] = 12 / itemsPerRowXl;
    }

    let baseColumnProps = { xs: { size: columnClasses[0] } };
    if (columnClasses[1] !== columnClasses[0]) {
      baseColumnProps.sm = { size: columnClasses[1] };
    }
    if (columnClasses[2] !== columnClasses[1]) {
      baseColumnProps.md = { size: columnClasses[2] };
    }
    if (columnClasses[3] !== columnClasses[2]) {
      baseColumnProps.lg = { size: columnClasses[3] };
    }
    if (columnClasses[4] !== columnClasses[3]) {
      baseColumnProps.xl = { size: columnClasses[4] };
    }

    setColumnProps(baseColumnProps);
  }, [itemsPerRowXs, itemsPerRowSm, itemsPerRowMd, itemsPerRowLg, itemsPerRowXl]);

  /* Set default value */
  useEffect(() => {
    !value && defaultValue && oc(defaultValue);
  }, [value, defaultValue]); // eslint-disable-line

  /* Set field error */
  useEffect(() => {
    if (!formState.errors) {
      setError(undefined);
    } else if (formState.errors[name] !== error) {
      setError(formState.errors[name]);
    }
  }, [formState, name, error]);

  if (variant === "card") {
    return (
      <CardRadioStyle error={invalid} innerRef={innerRef} {...spaceProps} {...typographyProps}>
        <Column xs={12}>
          {label && <FormLabelStyle>{label}{isRequired && ` *`}</FormLabelStyle>}
        </Column>

        {error && (
          <CardRadioErrorStyle sm={12}>
            {error.message}
          </CardRadioErrorStyle>
        )}

        {options.map((option, index) => (
          <Column
            key={option[valueProp]}
            {...columnProps}
            mt={{
              _: index >= localItemsPerRowXs ? "13px" : 0,
              sm: index >= localItemsPerRowSm ? "13px" : 0,
              md: index >= localItemsPerRowMd ? "13px" : 0,
              lg: index >= localItemsPerRowLg ? "13px" : 0,
              xl: index >= localItemsPerRowXl ? "13px" : 0,
            }}
            flex={[null, null, null, columnProps.lg?.size === 3 ? "0 0 25% !important" : null, null]}
            maxWidth={[null, null, null, columnProps.lg?.size === 3 ? "25% !important" : null, null]}
          >
            <CardRadioOption
              name={name}
              value={option[valueProp]}
              checked={value === option[valueProp]}
              error={invalid}
              onChange={onRadioChange}
              onBlur={ob}
              {...attributes}
              disabled={option.disabled || attributes.disabled}
            >{option[textProp]}</CardRadioOption>
          </Column>
        ))}

        {enableAddOption && (
          <Column
            {...columnProps}
            mt={{
              _: options.length >= localItemsPerRowXs ? "13px" : 0,
              sm: options.length >= localItemsPerRowSm ? "13px" : 0,
              md: options.length >= localItemsPerRowMd ? "13px" : 0,
              lg: options.length >= localItemsPerRowLg ? "13px" : 0,
              xl: options.length >= localItemsPerRowXl ? "13px" : 0,
            }}
            flex={[null, null, null, columnProps.lg?.size === 3 ? "0 0 25% !important" : null, null]}
            maxWidth={[null, null, null, columnProps.lg?.size === 3 ? "25% !important" : null, null]}
          >
            <CardRadioOptionStyle ref={addOptionContentRef} onClick={addOptionClickHandler} tabIndex="0" centered
                                  verticallyCentered>
              <div className="card-radio-option-content">
                <input type="radio" name={name} value="-1" style={{ display: "none" }} />
                <FontAwesomeIcon icon={faPlusCircle} style={{ fontSize: "16px" }} />
                {addOptionText && <div>{addOptionText}</div>}
              </div>
            </CardRadioOptionStyle>
          </Column>
        )}
      </CardRadioStyle>
    );
  }

  return (
    <FormRadioGroup {...spaceProps} {...typographyProps} ref={innerRef}>
      {options.map((option, index) => (
        <FormRadioWrapper key={option[valueProp]} inline={inline}>
          {!inline && <FormLabelStyle invalid={invalid}
                                      htmlFor={`${inputId}_${index}`}>{option[textProp]}{isRequired && ` *`}</FormLabelStyle>}
          <FormRadioStyle
            type="radio"
            name={name}
            value={option[valueProp]}
            id={`${inputId}_${index}`}
            checked={value === option[valueProp]}
            onChange={onRadioChange}
            onBlur={ob}
            inline={inline}
            {...attributes}
          />
          <FormRadioLabelStyle inline={inline} invalid={invalid} htmlFor={`${inputId}_${index}`}>
            {inline && <>{option[textProp]}{isRequired && ` *`}</>}
          </FormRadioLabelStyle>
        </FormRadioWrapper>
      ))}
      {helpText && (
        <>
          <div css={{ flexBasis: "100%", height: 0, marginBottom: !inline ? 26.5 : null }} />
          <FormHelpStyle>{helpText}</FormHelpStyle>
        </>
      )}
      {error && (
        <>
          {!inline && <div css={{ flexBasis: "100%", height: 0, marginBottom: !helpText ? 26.5 : null }} />}
          <FormInvalidFeedbackStyle role="alert">{error.message}</FormInvalidFeedbackStyle>
        </>
      )}
    </FormRadioGroup>
  );
};

FormRadio.propTypes = {
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  variant: PropTypes.oneOf(["", "card"]),
  validationRules: PropTypes.object,
  watch: PropTypes.func,
  control: PropTypes.object,
  formState: PropTypes.object,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  valueProp: PropTypes.string,
  textProp: PropTypes.string,
  defaultValue: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  id: PropTypes.string,
  helpText: PropTypes.string,
  onChange: PropTypes.func,
  ...propTypes.space,
  ...propTypes.typography,

  /* Regular radio props */
  inline: PropTypes.bool,

  /* Card radio props */
  itemsPerRowXs: PropTypes.number,
  itemsPerRowSm: PropTypes.number,
  itemsPerRowMd: PropTypes.number,
  itemsPerRowLg: PropTypes.number,
  itemsPerRowXl: PropTypes.number,
  enableAddOption: PropTypes.bool,
  addOptionText: PropTypes.string,
  onAddOptionClick: PropTypes.func,
};

FormRadio.defaultProps = {
  variant: "",
  disabled: false,
  valueProp: "value",
  textProp: "text",
  defaultValue: "",

  /* Regular radio props */
  inline: false,

  /* Card radio props */
  enableAddOption: false,
};

export default FormRadio;
